// Self-service identity hooks — the caller's own record + preferences (/api/me).
// Mirrors useApprovals/useDropinApi: TanStack Query over the functype apiClient, with
// mutations that invalidate the cached record. The server derives the user from the
// verified Principal, so none of these calls send a user id.

import type { MeResponse, UserPreferences } from "@civala/shared"
import { unwrapOrThrow } from "@civala/shared-ui/query"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiClient } from "@/lib/client"

const ME_KEY = ["me"] as const

const asMe = (raw: unknown): MeResponse => raw as MeResponse

export const useMe = () => {
  const query = useQuery({
    queryKey: ME_KEY,
    queryFn: () => apiClient.getEither("/api/me", { decode: asMe }).then(unwrapOrThrow),
  })
  return { me: query.data ?? null, loading: query.isLoading, error: query.error }
}

// Shallow-merge a preferences patch; the server returns the merged set. Invalidate so the
// cached record (and anything reading colorScheme/defaultProgram) refreshes.
export const useUpdatePreferences = () => {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (patch: UserPreferences) =>
      apiClient
        .putEither("/api/me/preferences", patch, { decode: (raw) => raw as { preferences: UserPreferences } })
        .then(unwrapOrThrow),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ME_KEY }),
  })
  return { updatePreferences: mutation.mutateAsync, saving: mutation.isPending }
}

export const useUpdateProfile = () => {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (displayName: string) =>
      apiClient.putEither("/api/me", { displayName }, { decode: asMe }).then(unwrapOrThrow),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ME_KEY }),
  })
  return { updateProfile: mutation.mutateAsync, saving: mutation.isPending }
}
